export type MarketingTrafficKind = "public" | "qa" | "internal" | "local";

export type MarketingTrafficContext = {
  analytics_page_path: string;
  page_host: string;
  page_path: string;
  page_path_query_redacted: string;
  traffic_kind: MarketingTrafficKind;
  analytics_excluded: boolean;
  analytics_exclusion_reason?: "explicit_qa_marker" | "explicit_internal_marker" | "local_or_non_production";
  marketing_analytics_schema_version: 1;
};

type MarketingTrafficContextInput = {
  environment: string;
  explicitInternalTraffic?: boolean;
  href: string;
};

const REDACTED_VALUE = "[REDACTED]";
const SENSITIVE_QUERY_KEYS = [
  "auth",
  "code",
  "distinct_id",
  "email",
  "handoff",
  "invite",
  "invitation",
  "jwt",
  "key",
  "landing",
  "manual",
  "otp",
  "pass",
  "password",
  "ph_distinct_id",
  "secret",
  "session",
  "state",
  "token",
];
const PRODUCT_ANALYTICS_HOSTS = new Set(["app.cooperly.ai", "app2.cooperly.ai"]);
const FORWARDED_TRAFFIC_KINDS = new Set(["internal", "qa"]);

function parseUrl(href: string): URL {
  try {
    return new URL(href);
  } catch {
    return new URL(href, "https://cooperly.ai");
  }
}

export function preserveProductAnalyticsTrafficMarker(
  targetHref: string,
  currentHref: string,
) {
  try {
    const currentUrl = new URL(currentHref);
    const targetUrl = new URL(targetHref, currentUrl.origin);
    const trafficKind = (
      currentUrl.searchParams.get("traffic_kind")
      ?? currentUrl.searchParams.get("cooperly_traffic_kind")
      ?? ""
    ).toLowerCase();

    if (
      !FORWARDED_TRAFFIC_KINDS.has(trafficKind)
      || !PRODUCT_ANALYTICS_HOSTS.has(targetUrl.hostname.toLowerCase())
    ) {
      return targetHref;
    }

    targetUrl.searchParams.set("traffic_kind", trafficKind);

    return targetUrl.toString();
  } catch {
    return targetHref;
  }
}

function isLocalHost(hostname: string) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function isProductionEnvironment(environment: string) {
  return environment === "production" || environment === "production2";
}

function isSensitiveQueryKey(key: string) {
  const normalized = key.toLowerCase().replace(/^[?&]+/, "");

  return SENSITIVE_QUERY_KEYS.some((sensitiveKey) => {
    return normalized === sensitiveKey || normalized.endsWith(`_${sensitiveKey}`);
  });
}

function redactedPathWithQuery(url: URL) {
  const search = url.search.replace(/^\?+/, "");
  const searchParams = new URLSearchParams(search);

  for (const key of Array.from(searchParams.keys())) {
    if (isSensitiveQueryKey(key)) {
      searchParams.set(key, REDACTED_VALUE);
    }
  }

  const query = searchParams.toString();

  return query === "" ? url.pathname || "/" : `${url.pathname || "/"}?${query}`;
}

function hasExplicitQaMarker(url: URL) {
  const explicitTrafficKind =
    url.searchParams.get("traffic_kind") ?? url.searchParams.get("cooperly_traffic_kind");

  if (explicitTrafficKind?.toLowerCase() === "qa") {
    return true;
  }

  const posthogDistinctId = url.searchParams.get("ph_distinct_id")?.toLowerCase() ?? "";

  if (
    posthogDistinctId.startsWith("qa-") ||
    posthogDistinctId.startsWith("qa_") ||
    posthogDistinctId.includes("qa-manual-")
  ) {
    return true;
  }

  return url.href.toLowerCase().includes("qa-manual-");
}

function hasExplicitInternalMarker(url: URL, explicitInternalTraffic: boolean) {
  if (explicitInternalTraffic) {
    return true;
  }

  const explicitTrafficKind =
    url.searchParams.get("traffic_kind") ?? url.searchParams.get("cooperly_traffic_kind");

  return explicitTrafficKind?.toLowerCase() === "internal";
}

function classifyTraffic(
  url: URL,
  environment: string,
  explicitInternalTraffic: boolean,
): Pick<MarketingTrafficContext, "traffic_kind" | "analytics_excluded" | "analytics_exclusion_reason"> {
  if (isLocalHost(url.hostname.toLowerCase()) || !isProductionEnvironment(environment)) {
    return {
      traffic_kind: "local",
      analytics_excluded: true,
      analytics_exclusion_reason: "local_or_non_production",
    };
  }

  if (hasExplicitInternalMarker(url, explicitInternalTraffic)) {
    return {
      traffic_kind: "internal",
      analytics_excluded: true,
      analytics_exclusion_reason: "explicit_internal_marker",
    };
  }

  if (hasExplicitQaMarker(url)) {
    return {
      traffic_kind: "qa",
      analytics_excluded: true,
      analytics_exclusion_reason: "explicit_qa_marker",
    };
  }

  return {
    traffic_kind: "public",
    analytics_excluded: false,
  };
}

export function buildMarketingTrafficContext({
  environment,
  explicitInternalTraffic = false,
  href,
}: MarketingTrafficContextInput): MarketingTrafficContext {
  const url = parseUrl(href);
  const classification = classifyTraffic(url, environment, explicitInternalTraffic);

  return {
    analytics_page_path: url.pathname || "/",
    page_host: url.hostname.toLowerCase(),
    page_path: url.pathname || "/",
    page_path_query_redacted: redactedPathWithQuery(url),
    marketing_analytics_schema_version: 1,
    ...classification,
  };
}

export function readExplicitInternalTrafficMarker() {
  if (typeof window === "undefined") {
    return false;
  }

  let localStorageValue: string | null = null;

  try {
    localStorageValue = window.localStorage?.getItem("cooperly_internal_traffic") ?? null;
  } catch {
    localStorageValue = null;
  }

  if (localStorageValue && ["1", "true", "yes"].includes(localStorageValue.toLowerCase())) {
    return true;
  }

  try {
    return document.cookie
      .split(";")
      .some((segment) => segment.trim().toLowerCase() === "cooperly_internal_traffic=1");
  } catch {
    return false;
  }
}

export function currentMarketingTrafficContext(environment: string) {
  const href =
    typeof window === "undefined" ? "https://cooperly.ai/" : window.location.href;

  return buildMarketingTrafficContext({
    environment,
    explicitInternalTraffic: readExplicitInternalTrafficMarker(),
    href,
  });
}
